import { MaterialCommunityIcons } from "@expo/vector-icons";
import { LinearGradient } from "expo-linear-gradient";
import { useFocusEffect, useLocalSearchParams, useRouter } from "expo-router";
import { useCallback, useState } from "react";
import {
    ActivityIndicator,
    Platform,
    Pressable,
    ScrollView,
    StyleSheet,
    TextInput,
    TouchableOpacity,
    View
} from "react-native";
import { Text } from "react-native-paper";
import { SafeAreaView } from "react-native-safe-area-context";

import { ComplianceCircleIcon, STATUS_VISUAL } from "@/components/compliance/ComplianceItemCard";
import { ContractReader, isReaderForm } from "@/components/compliance/ContractReader";
import { ContractResign } from "@/components/compliance/ContractResign";
import { RenewalStagingTray } from "@/components/compliance/RenewalStagingTray";
import { UploadSourceSheet, type UploadSource } from "@/components/compliance/UploadSourceSheet";
import { WebSignaturePad } from "@/components/onboarding/inputs/WebSignaturePad";
import { SignatureModal } from "@/components/questions/SignatureModal";
import { useAuth } from "@/lib/auth";
import {
    docStatusToType,
    docStatusLabel,
    fetchRnComplianceDoc,
    renewComplianceCombined,
    signContract,
    submitComplianceFormAnswers,
    type ComplianceFormAnswer,
    type ComplianceFormQuestion,
    type ComplianceItemField,
    type ComplianceRecord,
    type RnComplianceDocDetail,
    type RnPendingUpload
} from "@/lib/compliance-api";
import { contractSignState } from "@/lib/contract-sign-state";
import { colors, fonts, gradients, radius, spacing, typography } from "@/lib/theme";
import { pickFromCamera, pickFromPhotoLibrary, pickPdfDocument } from "@/utils/pick-upload";

// The RN contract is a `signature` item with no compliance_form_question, so it
// has no real question id to open the shared signature pad with. This sentinel
// stands in for one. Unlike form signatures it never sits in `formAnswers`:
// confirming the pad submits the contract at once.
const CONTRACT_SIG_KEY = -1;

function fmtDate(iso: string | null): string {
    if (!iso) {
        return "—";
    }
    // Date-only strings ("2026-06-04") get a calendar-day render — do NOT
    // parse them through `new Date()`, which treats the bare string as UTC
    // midnight and renders as the day before in any zone west of UTC. Build
    // the Date from the components instead and pin the locale to en-US so
    // the format is stable across devices ("Jun 4, 2026" everywhere).
    const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
    const d = dateOnly
        ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
        : new Date(iso);
    if (Number.isNaN(d.getTime())) {
        return "—";
    }
    return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

/** A pending upload (under review) — rendered above the document history. */
function PendingRow({ upload }: { upload: RnPendingUpload }) {
    const isRejected = upload.status === "rejected" || upload.status === "verification_failed";
    const subtitle = isRejected
        ? upload.rejectionReason
            ? `Rejected · ${upload.rejectionReason}`
            : "Rejected"
        : "Pending review";
    return (
        <View style={styles.docRow}>
            <ComplianceCircleIcon statusType={isRejected ? "issue" : "pending"} size={32} />
            <View style={styles.docBody}>
                <Text style={styles.docTitle}>
                    Uploaded {upload.submittedAt ? fmtDate(upload.submittedAt) : "just now"}
                </Text>
                <Text style={styles.docSub}>{subtitle}</Text>
            </View>
        </View>
    );
}

/** A single uploaded record in the document-history list. */
function DocumentRow({
    record,
    fields
}: {
    record: ComplianceRecord;
    fields: ComplianceItemField[];
}) {
    // Expiry-aware bucketing so a still-compliant-but-expiring record
    // renders as amber, not green.
    const type = docStatusToType(record.status, { expiryDate: record.expiryDate });
    const label = docStatusLabel(record.status, { expiryDate: record.expiryDate });
    // Only append the expiry half when there's a real date AND the record is
    // in a state where expiry is meaningful. For `missing` / `not_compliant`
    // / `pending_uploads`, "· Doesn't expire" is misleading (the document
    // will pick up an expiry once uploaded/renewed); show just the label.
    const showExpiry =
        record.status === "compliant" ||
        record.status === "not_due_yet" ||
        record.status === "resolved";
    const expirySuffix =
        showExpiry && record.expiryDate ? ` · Expires ${fmtDate(record.expiryDate)}` : "";
    return (
        <View style={styles.docRow}>
            <ComplianceCircleIcon statusType={type} size={32} />
            <View style={styles.docBody}>
                <Text style={styles.docTitle}>Uploaded {fmtDate(record.createdAt)}</Text>
                <Text style={styles.docSub}>
                    {label}
                    {expirySuffix}
                </Text>
                {record.rejectionReason ? (
                    <Text style={styles.docReject}>Rejected: {record.rejectionReason}</Text>
                ) : null}
                {record.fieldValues.map((fv) => {
                    const def = fields.find((f) => f.id === fv.fieldId);
                    return (
                        <Text key={fv.fieldId} style={styles.docField}>
                            {def?.label ?? "Field"}: {fv.valueText ?? "—"}
                        </Text>
                    );
                })}
            </View>
        </View>
    );
}

/**
 * compliance_form renderer. Walks the authored compliance_form_question
 * list and renders one widget per type:
 *
 *   plain_text → prose paragraph (no input).
 *   free_text  → TextInput.
 *   radio      → row of chips, exactly one selected.
 *   checkbox   → row of chips, multi-select.
 *   signature  → "Sign" button that opens SignatureModal; once captured,
 *                the chip flips to "Signed ✓" with a re-sign affordance.
 *
 * Answer state lives in the parent so submit can read it on tap.
 */
function ComplianceFormSection({
    questions,
    answers,
    setAnswers,
    onRequestSignature
}: {
    questions: ComplianceFormQuestion[];
    answers: Record<number, { valueText?: string; choiceIds?: number[] }>;
    setAnswers: (next: Record<number, { valueText?: string; choiceIds?: number[] }>) => void;
    onRequestSignature: (questionId: number) => void;
}) {
    function setForQ(questionId: number, patch: { valueText?: string; choiceIds?: number[] }) {
        setAnswers({
            ...answers,
            [questionId]: { ...(answers[questionId] ?? {}), ...patch }
        });
    }

    function togglePick(q: ComplianceFormQuestion, choiceId: number) {
        const current = answers[q.id]?.choiceIds ?? [];
        if (q.questionType === "radio") {
            // Radio: single-pick, replace whatever was there.
            setForQ(q.id, { choiceIds: [choiceId] });
            return;
        }
        // Checkbox: toggle membership.
        const next = current.includes(choiceId)
            ? current.filter((id) => id !== choiceId)
            : [...current, choiceId];
        setForQ(q.id, { choiceIds: next });
    }

    return (
        <View style={styles.historyCard}>
            {questions.map((q, i) => {
                const current = answers[q.id] ?? {};
                return (
                    <View key={q.id}>
                        {i > 0 ? <View style={styles.divider} /> : null}
                        <View style={styles.formField}>
                            {q.questionType === "plain_text" ? (
                                <Text style={styles.formProse}>{q.label}</Text>
                            ) : (
                                <Text style={styles.formLabel}>
                                    {q.label}
                                    {q.mandatory ? " *" : ""}
                                </Text>
                            )}

                            {q.questionType === "free_text" && (
                                <TextInput
                                    style={styles.formInput}
                                    placeholder="Type your answer"
                                    placeholderTextColor={colors.textTertiary}
                                    value={current.valueText ?? ""}
                                    onChangeText={(t) => setForQ(q.id, { valueText: t })}
                                    autoCapitalize="sentences"
                                />
                            )}

                            {(q.questionType === "radio" || q.questionType === "checkbox") && (
                                <View style={styles.choiceList}>
                                    {q.choices.map((c) => {
                                        const picked = (current.choiceIds ?? []).includes(c.id);
                                        return (
                                            <Pressable
                                                key={c.id}
                                                onPress={() => togglePick(q, c.id)}
                                                style={[
                                                    styles.choiceItem,
                                                    picked && styles.choicePicked
                                                ]}
                                            >
                                                <Text
                                                    style={[
                                                        styles.choiceLabel,
                                                        picked && styles.choicePickedText
                                                    ]}
                                                >
                                                    {c.label}
                                                </Text>
                                            </Pressable>
                                        );
                                    })}
                                </View>
                            )}

                            {q.questionType === "signature" && (
                                <View style={styles.signatureBlock}>
                                    {current.valueText ? (
                                        <View style={styles.signedRow}>
                                            <MaterialCommunityIcons
                                                name="check-circle"
                                                size={18}
                                                color={colors.teal600}
                                            />
                                            <Text style={styles.signedText}>Signed</Text>
                                            <Pressable
                                                onPress={() => onRequestSignature(q.id)}
                                                style={styles.signLink}
                                            >
                                                <Text style={styles.signLinkText}>Re-sign</Text>
                                            </Pressable>
                                        </View>
                                    ) : (
                                        <Pressable
                                            onPress={() => onRequestSignature(q.id)}
                                            style={styles.signBtn}
                                        >
                                            <MaterialCommunityIcons
                                                name="draw"
                                                size={16}
                                                color={colors.surface}
                                            />
                                            <Text style={styles.signBtnText}>Tap to sign</Text>
                                        </Pressable>
                                    )}
                                </View>
                            )}
                        </View>
                    </View>
                );
            })}
        </View>
    );
}

export default function ComplianceDocDetailScreen() {
    const router = useRouter();
    const { user } = useAuth();
    const { docId } = useLocalSearchParams<{ docId: string }>();
    const rnId = user?.rnId ? String(user.rnId) : "";

    const [detail, setDetail] = useState<RnComplianceDocDetail | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [renewing, setRenewing] = useState(false);
    const [renewError, setRenewError] = useState<string | null>(null);
    /**
     * Whether the upload-source sheet (Camera / Photos / PDF) is open.
     * Tapping the "Renew" button opens the sheet for the FIRST file. Once
     * one file is staged the staging modal takes over and the sheet
     * reappears on each "Add another" tap.
     */
    const [sourceSheetOpen, setSourceSheetOpen] = useState(false);
    /**
     * Files the nurse has selected for this renewal but not yet submitted.
     * `id` is local-only — used to remove items from the staging list. All
     * staged files ship as ONE combined upload via the new combined-renewal
     * endpoint when the nurse taps Submit.
     */
    const [stagedFiles, setStagedFiles] = useState<
        Array<{ id: string; uri: string; contentType: string; filename: string }>
    >([]);
    /** Working draft of the form answers (compliance_form items). Keyed by questionId. */
    const [formAnswers, setFormAnswers] = useState<
        Record<number, { valueText?: string; choiceIds?: number[] }>
    >({});
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    /** Which signature question is currently being captured (modal open). */
    const [signatureForQuestion, setSignatureForQuestion] = useState<number | null>(null);
    /**
     * Whether the pad was last opened for the contract. Unlike
     * `signatureForQuestion` it is not cleared on close, so the confirm label
     * doesn't flip from "Submit" to "Done" while the pad animates away.
     */
    const [padSubmitsContract, setPadSubmitsContract] = useState(false);
    const openSignaturePad = useCallback((key: number) => {
        setPadSubmitsContract(key === CONTRACT_SIG_KEY);
        setSignatureForQuestion(key);
    }, []);

    const load = useCallback(async () => {
        if (!rnId || !docId) {
            return;
        }
        setLoading(true);
        setError(null);
        try {
            setDetail(await fetchRnComplianceDoc(rnId, docId));
        } catch (e) {
            setError(e instanceof Error ? e.message : "Failed to load document");
        } finally {
            setLoading(false);
        }
    }, [rnId, docId]);

    /**
     * Renewal: open the Camera / Photos / PDF chooser for the next file to
     * stage. Same sheet used for the initial pick and for every subsequent
     * "Add another". The actual submit happens later in `submitRenewal`.
     */
    const handleRenew = useCallback(() => {
        if (!rnId || !detail) {
            return;
        }
        setRenewError(null);
        setSourceSheetOpen(true);
    }, [rnId, detail]);

    /**
     * Append picked files to the staging list. The photo + PDF pickers are
     * multi-select, so one call can return N files — the nurse picks every
     * page of the document in one shot. Camera stays single-shot.
     */
    const stageFileFromSource = useCallback(async (source: UploadSource) => {
        const result =
            source === "camera"
                ? await pickFromCamera()
                : source === "photo"
                  ? await pickFromPhotoLibrary()
                  : await pickPdfDocument();
        // Permission denied → Settings hint. Plain cancel is silent.
        if (result.status === "denied") {
            setRenewError(
                source === "camera"
                    ? "Camera access is needed. Grant it in Settings to take a photo."
                    : "Photo library access is needed. Grant it in Settings to choose a photo."
            );
            return;
        }
        if (result.status === "cancelled") {
            return;
        }
        setStagedFiles((prev) => [
            ...prev,
            ...result.value.map((picked, i) => ({
                id: `${Date.now()}_${i}_${Math.random().toString(36).slice(2, 6)}`,
                uri: picked.uri,
                contentType: picked.contentType,
                filename: picked.filename
            }))
        ]);
    }, []);

    /** Remove a staged file before submit. */
    const removeStagedFile = useCallback((id: string) => {
        setStagedFiles((prev) => prev.filter((f) => f.id !== id));
    }, []);

    /**
     * Ship every staged file in one multipart POST to the combined
     * renewal endpoint. The backend stitches them into a single PDF (or
     * keeps the lone file as-is) and creates ONE upload row + ONE pending
     * review item — so the admin sees a single combined artifact, not N.
     */
    const submitRenewal = useCallback(async () => {
        if (!rnId || !detail || stagedFiles.length === 0) {
            return;
        }
        setRenewing(true);
        setRenewError(null);
        try {
            await renewComplianceCombined(
                rnId,
                detail.itemId,
                stagedFiles.map((f) => ({
                    uri: f.uri,
                    contentType: f.contentType,
                    filename: f.filename
                }))
            );
            setStagedFiles([]);
            await load();
        } catch (e) {
            setRenewError(e instanceof Error ? e.message : "Renewal failed");
        } finally {
            setRenewing(false);
        }
    }, [rnId, detail, stagedFiles, load]);

    /** Drop every staged file — clean exit from a half-built renewal. */
    const cancelRenewal = useCallback(() => {
        setStagedFiles([]);
        setRenewError(null);
    }, []);

    /** compliance_form items — submit the typed/picked answers, refresh detail. */
    const handleSubmitForm = useCallback(async () => {
        if (!rnId || !detail) {
            return;
        }
        setSubmitError(null);

        // Build the payload, dropping plain_text questions (display-only).
        const answers: ComplianceFormAnswer[] = (detail.questions ?? [])
            .filter((q) => q.questionType !== "plain_text")
            .map((q) => {
                const a = formAnswers[q.id] ?? {};
                return {
                    questionId: q.id,
                    valueText: a.valueText ?? null,
                    choiceIds: a.choiceIds ?? []
                };
            })
            // Strip empty entries so we don't waste bytes — backend treats absent
            // questions identically to empty ones.
            .filter(
                (a) =>
                    (a.valueText !== null && (a.valueText ?? "").trim() !== "") ||
                    (a.choiceIds ?? []).length > 0
            );

        // Client-side mandatory check — backend re-validates for safety.
        const missing = (detail.questions ?? []).find((q) => {
            if (!q.mandatory || q.questionType === "plain_text") {
                return false;
            }
            const a = formAnswers[q.id] ?? {};
            if (q.questionType === "radio" || q.questionType === "checkbox") {
                return (a.choiceIds ?? []).length === 0;
            }
            return !a.valueText || a.valueText.trim() === "";
        });
        if (missing) {
            setSubmitError(`Please answer "${missing.label}" before submitting.`);
            return;
        }
        setSubmitting(true);
        try {
            await submitComplianceFormAnswers(rnId, detail.itemId, answers);
            setFormAnswers({});
            await load();
        } catch (e) {
            setSubmitError(e instanceof Error ? e.message : "Submit failed");
        } finally {
            setSubmitting(false);
        }
    }, [rnId, detail, formAnswers, load]);

    /**
     * Contract re-sign — called straight from the signature pad's Submit, so
     * drawing the signature IS submitting it. Refreshing the detail then shows
     * the signed state.
     */
    const handleSignContract = useCallback(
        async (signature: string) => {
            if (!rnId || submitting) {
                return;
            }
            setSubmitError(null);
            setSubmitting(true);
            try {
                await signContract(rnId, signature);
                await load();
            } catch (e) {
                setSubmitError(e instanceof Error ? e.message : "Submit failed");
            } finally {
                setSubmitting(false);
            }
        },
        [rnId, submitting, load]
    );

    useFocusEffect(
        useCallback(() => {
            load();
        }, [load])
    );

    const active = detail?.activeRecord ?? null;
    const pendingReview = (detail?.pendingUploads ?? []).filter(
        (p) => p.status === "submitted" || p.status === "ai_verifying"
    );
    // If there's an in-flight upload, reflect "Pending review" in the hero —
    // even when there's a still-compliant prior record. RNs care most about
    // the action that's still in-flight; surfacing the prior approval would
    // make them think the resubmit had already been accepted.
    const heroIsPending = pendingReview.length > 0;
    // statusType now considers expiry too — a compliant doc within the
    // warn window resolves as "expiringSoon" so the icon/chrome warn.
    const statusType = heroIsPending
        ? "pending"
        : active
          ? docStatusToType(active.status, { expiryDate: active.expiryDate })
          : "pending";

    return (
        <SafeAreaView edges={["top", "bottom"]} style={styles.container}>
            <View style={styles.topBar}>
                <TouchableOpacity
                    style={styles.backArrow}
                    hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
                    onPress={() => {
                        if (router.canGoBack()) {
                            router.back();
                        } else {
                            router.replace("/compliance");
                        }
                    }}
                >
                    <MaterialCommunityIcons
                        name="chevron-left"
                        size={28}
                        color={colors.textPrimary}
                    />
                </TouchableOpacity>
            </View>

            <ScrollView contentContainerStyle={styles.scroll}>
                <Text style={styles.header}>{detail?.itemName ?? "Document"}</Text>

                {loading && (
                    <View style={styles.center}>
                        <ActivityIndicator size="large" color={colors.teal400} />
                    </View>
                )}

                {error && !loading && (
                    <View style={styles.center}>
                        <MaterialCommunityIcons
                            name="alert-circle-outline"
                            size={44}
                            color={colors.error500}
                        />
                        <Text style={styles.errorText}>{error}</Text>
                        <TouchableOpacity onPress={load}>
                            <LinearGradient
                                colors={[...gradients.brand]}
                                start={{ x: 0, y: 0 }}
                                end={{ x: 1, y: 0 }}
                                style={styles.retryBtn}
                            >
                                <Text style={styles.retryText}>Retry</Text>
                            </LinearGradient>
                        </TouchableOpacity>
                    </View>
                )}

                {detail &&
                    !loading &&
                    !error &&
                    (() => {
                        // The RN contract: a `signature` item whose real agreement
                        // text we render as a read-and-sign document (not an upload).
                        // Owns its own submit button, so it replaces the whole body.
                        if (detail.contractMarkdown != null) {
                            const contract = contractSignState(active);
                            return (
                                <ContractResign
                                    markdown={detail.contractMarkdown}
                                    signed={contract.signed}
                                    signedOn={contract.signedAt ? fmtDate(contract.signedAt) : null}
                                    onSign={() => openSignaturePad(CONTRACT_SIG_KEY)}
                                    submitting={submitting}
                                    submitError={submitError}
                                />
                            );
                        }
                        const isForm = detail.itemType === "compliance_form";
                        const hasInFlightForm = isForm && pendingReview.length > 0;
                        return (
                            <>
                                {/* Status hero card */}
                                <View
                                    style={[
                                        styles.statusCard,
                                        { backgroundColor: STATUS_VISUAL[statusType].bg }
                                    ]}
                                >
                                    <ComplianceCircleIcon statusType={statusType} size={52} />
                                    <Text style={styles.statusLabel}>
                                        {heroIsPending
                                            ? "Pending review"
                                            : active
                                              ? docStatusLabel(active.status, {
                                                    expiryDate: active.expiryDate
                                                })
                                              : "No document on file"}
                                    </Text>
                                    {/* Only show effective/expiry for a record that
                                    represents a real document. A "missing" credential has
                                    no active doc — its dates are stale placeholder values,
                                    so showing a future "Expires" there is misleading. */}
                                    {!heroIsPending && active && active.status !== "missing" ? (
                                        <Text style={styles.statusDates}>
                                            {active.effectiveDate
                                                ? `Effective ${fmtDate(active.effectiveDate)}`
                                                : ""}
                                            {active.effectiveDate && active.expiryDate
                                                ? "  ·  "
                                                : ""}
                                            {active.expiryDate
                                                ? `Expires ${fmtDate(active.expiryDate)}`
                                                : active.effectiveDate
                                                  ? ""
                                                  : "Doesn't expire"}
                                        </Text>
                                    ) : null}
                                </View>

                                {/* Crawler-verified credentials (RN license): we re-check
                                    the state board daily, so the RN never uploads anything.
                                    Reassure them in place of the upload CTA. */}
                                {detail.autoVerified && (
                                    <View
                                        style={{
                                            flexDirection: "row",
                                            gap: 10,
                                            alignItems: "flex-start",
                                            backgroundColor: colors.teal50,
                                            borderColor: colors.teal200,
                                            borderWidth: 1,
                                            borderRadius: radius.md,
                                            padding: spacing.base,
                                            marginTop: spacing.md
                                        }}
                                    >
                                        <MaterialCommunityIcons
                                            name="shield-check-outline"
                                            size={20}
                                            color={colors.teal600}
                                        />
                                        <Text
                                            style={{
                                                flex: 1,
                                                fontFamily: fonts.medium,
                                                fontSize: 13,
                                                lineHeight: 18,
                                                color: colors.textSecondary
                                            }}
                                        >
                                            We verify your license automatically with New York State
                                            every day — you don't need to upload anything. We'll
                                            only flag it if it can't be confirmed.
                                        </Text>
                                    </View>
                                )}

                                {/* compliance_form items — interactive form. Hidden once a
                            submission is in flight (status hero shows "Pending review"),
                            and when the credential is already compliant + in-date
                            (canSubmit=false) so it can't be needlessly re-submitted. */}
                                {isForm &&
                                    !hasInFlightForm &&
                                    detail.canSubmit &&
                                    detail.questions.length > 0 &&
                                    (isReaderForm(detail.questions) ? (
                                        // Read-and-sign document (e.g. the RN contract): paged
                                        // reader with its own Back/Next + submit, so no
                                        // section title or footer CTA here.
                                        <ContractReader
                                            questions={detail.questions}
                                            answers={formAnswers}
                                            onRequestSignature={openSignaturePad}
                                            onSubmit={handleSubmitForm}
                                            submitting={submitting}
                                            submitError={submitError}
                                            submitLabel={active ? "Re-submit" : "Accept & Submit"}
                                        />
                                    ) : (
                                        <>
                                            <Text style={styles.sectionTitle}>
                                                {active ? "Re-submit form" : "Fill the form"}
                                            </Text>
                                            <ComplianceFormSection
                                                questions={detail.questions}
                                                answers={formAnswers}
                                                setAnswers={setFormAnswers}
                                                onRequestSignature={openSignaturePad}
                                            />
                                            {submitError ? (
                                                <Text style={styles.renewError}>{submitError}</Text>
                                            ) : null}
                                        </>
                                    ))}

                                {/* Pending vs rejected get their own sections so
                                   the heading matches what's inside (a rejected
                                   row under "Pending review" was reading as
                                   contradictory). */}
                                {(() => {
                                    const pendingUploads = detail.pendingUploads.filter(
                                        (p) =>
                                            p.status === "submitted" ||
                                            p.status === "ai_verifying" ||
                                            p.status === "verification_failed"
                                    );
                                    const rejectedUploads = detail.pendingUploads.filter(
                                        (p) => p.status === "rejected"
                                    );
                                    return (
                                        <>
                                            {pendingUploads.length > 0 && (
                                                <>
                                                    <Text style={styles.sectionTitle}>
                                                        Pending review
                                                    </Text>
                                                    <View style={styles.historyCard}>
                                                        {pendingUploads.map((p, i) => (
                                                            <View key={p.submissionId}>
                                                                {i > 0 ? (
                                                                    <View style={styles.divider} />
                                                                ) : null}
                                                                <PendingRow upload={p} />
                                                            </View>
                                                        ))}
                                                    </View>
                                                </>
                                            )}
                                            {rejectedUploads.length > 0 && (
                                                <>
                                                    <Text style={styles.sectionTitle}>
                                                        Rejected
                                                    </Text>
                                                    <View style={styles.historyCard}>
                                                        {rejectedUploads.map((p, i) => (
                                                            <View key={p.submissionId}>
                                                                {i > 0 ? (
                                                                    <View style={styles.divider} />
                                                                ) : null}
                                                                <PendingRow upload={p} />
                                                            </View>
                                                        ))}
                                                    </View>
                                                </>
                                            )}
                                        </>
                                    );
                                })()}

                                {/* Document history — only records with a real upload */}
                                {(() => {
                                    const history = [
                                        ...(detail.activeRecord ? [detail.activeRecord] : []),
                                        ...detail.previousRecords
                                    ].filter((r) => r.uploadId != null);
                                    if (history.length === 0) {
                                        return null;
                                    }
                                    return (
                                        <>
                                            <Text style={styles.sectionTitle}>
                                                Document history
                                            </Text>
                                            <View style={styles.historyCard}>
                                                {history.map((rec, i) => (
                                                    <View key={rec.id}>
                                                        {i > 0 ? (
                                                            <View style={styles.divider} />
                                                        ) : null}
                                                        <DocumentRow
                                                            record={rec}
                                                            fields={detail.fields}
                                                        />
                                                    </View>
                                                ))}
                                            </View>
                                        </>
                                    );
                                })()}
                            </>
                        );
                    })()}
            </ScrollView>

            {detail && !loading && !error
                ? (() => {
                      // The contract renders its own submit inside the body.
                      if (detail.contractMarkdown != null) {
                          return null;
                      }
                      const isForm = detail.itemType === "compliance_form";
                      const hasInFlightForm = isForm && pendingReview.length > 0;
                      // Already compliant + not yet due for renewal — no resubmission
                      // CTA. Show a reassuring note instead.
                      if (!detail.canSubmit) {
                          return (
                              <View style={styles.footer}>
                                  <View style={styles.lockedNote}>
                                      <MaterialCommunityIcons
                                          name="shield-check"
                                          size={18}
                                          color={colors.success600}
                                      />
                                      <Text style={styles.lockedNoteText}>
                                          This credential is compliant — no action needed right now.
                                      </Text>
                                  </View>
                              </View>
                          );
                      }
                      // Form mode: the bottom CTA submits answers, not an upload.
                      // Suppressed while a submission is already in flight, and for
                      // read-and-sign documents (ContractReader owns its own submit).
                      if (isForm) {
                          if (hasInFlightForm || isReaderForm(detail.questions)) {
                              return null;
                          }
                          return (
                              <View style={styles.footer}>
                                  <TouchableOpacity
                                      activeOpacity={0.85}
                                      disabled={submitting}
                                      onPress={handleSubmitForm}
                                  >
                                      <LinearGradient
                                          colors={[...gradients.brand]}
                                          start={{ x: 0, y: 0 }}
                                          end={{ x: 1, y: 0 }}
                                          style={styles.renewBtn}
                                      >
                                          {submitting ? (
                                              <ActivityIndicator
                                                  size="small"
                                                  color={colors.surface}
                                              />
                                          ) : (
                                              <>
                                                  <MaterialCommunityIcons
                                                      name="check-circle-outline"
                                                      size={18}
                                                      color={colors.surface}
                                                  />
                                                  <Text style={styles.renewBtnText}>
                                                      {detail.activeRecord ? "Re-submit" : "Submit"}
                                                  </Text>
                                              </>
                                          )}
                                      </LinearGradient>
                                  </TouchableOpacity>
                              </View>
                          );
                      }
                      // Upload mode (default).
                      return (
                          <View style={styles.footer}>
                              {renewError ? (
                                  <Text style={styles.renewError}>{renewError}</Text>
                              ) : null}
                              <TouchableOpacity
                                  activeOpacity={0.85}
                                  disabled={renewing}
                                  onPress={handleRenew}
                              >
                                  <LinearGradient
                                      colors={[...gradients.brand]}
                                      start={{ x: 0, y: 0 }}
                                      end={{ x: 1, y: 0 }}
                                      style={styles.renewBtn}
                                  >
                                      {renewing ? (
                                          <ActivityIndicator size="small" color={colors.surface} />
                                      ) : (
                                          <>
                                              <MaterialCommunityIcons
                                                  name="cloud-upload-outline"
                                                  size={18}
                                                  color={colors.surface}
                                              />
                                              <Text style={styles.renewBtnText}>
                                                  {detail.activeRecord
                                                      ? "Renew document"
                                                      : "Upload document"}
                                              </Text>
                                          </>
                                      )}
                                  </LinearGradient>
                              </TouchableOpacity>
                          </View>
                      );
                  })()
                : null}

            {(() => {
                const confirmLabel = padSubmitsContract ? "Submit" : undefined;
                const onSign = (base64: string) => {
                    if (signatureForQuestion === CONTRACT_SIG_KEY) {
                        setSignatureForQuestion(null);
                        void handleSignContract(base64);
                        return;
                    }
                    if (signatureForQuestion != null) {
                        setFormAnswers((prev) => ({
                            ...prev,
                            [signatureForQuestion]: {
                                ...(prev[signatureForQuestion] ?? {}),
                                valueText: base64
                            }
                        }));
                    }
                    setSignatureForQuestion(null);
                };
                // Web: real canvas capture (SignatureModal's WebView pad doesn't
                // capture on web). Native keeps SignatureModal.
                return Platform.OS === "web" ? (
                    <WebSignaturePad
                        visible={signatureForQuestion != null}
                        title="Sign to submit"
                        confirmLabel={confirmLabel}
                        onClose={() => setSignatureForQuestion(null)}
                        onDone={onSign}
                    />
                ) : (
                    <SignatureModal
                        visible={signatureForQuestion != null}
                        title="Sign to submit"
                        confirmLabel={confirmLabel}
                        onClose={() => setSignatureForQuestion(null)}
                        onDone={onSign}
                    />
                );
            })()}

            {/* Upload-source chooser for the Renew flow (Camera / Photos / PDF). */}
            <UploadSourceSheet
                visible={sourceSheetOpen}
                onClose={() => setSourceSheetOpen(false)}
                onPick={(source) => {
                    void stageFileFromSource(source);
                }}
                title={detail ? `Upload ${detail.itemName ?? "document"}` : "Upload document"}
            />

            {/*
             * Multi-file staging tray. Visible while the nurse has at least
             * one file staged for renewal but hasn't submitted yet. Files
             * are submitted as ONE combined upload — the backend stitches
             * images + PDFs into a single artifact. No "add more" button on
             * purpose: the pickers themselves are multi-select, and stacking
             * a second modal on top of this one froze the simulator.
             */}
            <RenewalStagingTray
                files={stagedFiles}
                uploading={renewing}
                onRemove={removeStagedFile}
                onSubmit={() => void submitRenewal()}
                onCancel={cancelRenewal}
            />
        </SafeAreaView>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: colors.background
    },
    topBar: {
        flexDirection: "row",
        alignItems: "center",
        paddingHorizontal: spacing.sm,
        paddingTop: spacing.xs
    },
    backArrow: {
        width: 44,
        height: 44,
        alignItems: "center",
        justifyContent: "center"
    },
    scroll: {
        paddingHorizontal: spacing.screenPadding,
        paddingBottom: spacing["3xl"],
        gap: spacing.md
    },
    header: {
        ...typography.h1,
        color: colors.textPrimary
    },
    center: {
        alignItems: "center",
        justifyContent: "center",
        gap: spacing.md,
        paddingVertical: spacing["4xl"]
    },
    errorText: {
        ...typography.body,
        color: colors.error500,
        textAlign: "center"
    },
    retryBtn: {
        paddingHorizontal: spacing.lg,
        paddingVertical: spacing.sm + 2,
        borderRadius: radius.full
    },
    retryText: {
        fontFamily: fonts.semiBold,
        fontSize: 14,
        color: colors.surface
    },
    statusCard: {
        alignItems: "center",
        gap: spacing.sm,
        borderRadius: radius.lg,
        padding: spacing.xl
    },
    statusLabel: {
        ...typography.h2,
        color: colors.textPrimary
    },
    statusDates: {
        ...typography.bodySmall,
        color: colors.textSecondary,
        textAlign: "center"
    },
    sectionTitle: {
        ...typography.subtitle,
        color: colors.textPrimary,
        marginTop: spacing.sm
    },
    emptyHistory: {
        ...typography.body,
        color: colors.textTertiary
    },
    historyCard: {
        backgroundColor: colors.surface,
        borderRadius: radius.md,
        borderWidth: 1,
        borderColor: colors.border,
        paddingHorizontal: spacing.base
    },
    divider: {
        height: 1,
        backgroundColor: colors.border
    },
    docRow: {
        flexDirection: "row",
        alignItems: "flex-start",
        gap: spacing.md,
        paddingVertical: spacing.base
    },
    docBody: {
        flex: 1,
        gap: 2
    },
    docTitle: {
        fontSize: 14,
        fontFamily: fonts.bold,
        color: colors.textPrimary
    },
    docSub: {
        fontSize: 13,
        fontFamily: fonts.medium,
        color: colors.textSecondary
    },
    docReject: {
        fontSize: 12,
        fontFamily: fonts.medium,
        color: colors.error600,
        marginTop: 2
    },
    docField: {
        fontSize: 12,
        fontFamily: fonts.medium,
        color: colors.textTertiary,
        marginTop: 2
    },
    footer: {
        paddingHorizontal: spacing.screenPadding,
        paddingTop: spacing.sm,
        paddingBottom: spacing.sm,
        gap: spacing.sm,
        borderTopWidth: 1,
        borderTopColor: colors.border,
        backgroundColor: colors.surface
    },
    renewBtn: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: spacing.sm,
        height: 50,
        borderRadius: radius.md
    },
    renewBtnText: {
        fontSize: 15,
        fontFamily: fonts.bold,
        color: colors.surface
    },
    renewError: {
        ...typography.bodySmall,
        color: colors.error500,
        textAlign: "center"
    },
    lockedNote: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: spacing.xs
    },
    lockedNoteText: {
        ...typography.bodySmall,
        color: colors.success600,
        flexShrink: 1
    },
    formField: {
        paddingVertical: spacing.base,
        gap: spacing.xs
    },
    formLabel: {
        fontSize: 13,
        fontFamily: fonts.bold,
        color: colors.textPrimary
    },
    formInput: {
        borderWidth: 1,
        borderColor: colors.border,
        borderRadius: radius.sm,
        paddingHorizontal: spacing.sm,
        paddingVertical: spacing.xs + 2,
        fontFamily: fonts.medium,
        fontSize: 14,
        color: colors.textPrimary,
        backgroundColor: colors.background
    },
    choiceList: {
        flexDirection: "row",
        flexWrap: "wrap",
        gap: spacing.xs
    },
    choiceItem: {
        paddingHorizontal: spacing.sm + 2,
        paddingVertical: spacing.xs + 2,
        borderRadius: radius.full,
        borderWidth: 1,
        borderColor: colors.border,
        backgroundColor: colors.background
    },
    choicePicked: {
        backgroundColor: colors.teal400,
        borderColor: colors.teal400
    },
    choiceLabel: {
        fontFamily: fonts.medium,
        fontSize: 13,
        color: colors.textPrimary
    },
    choicePickedText: {
        color: colors.surface
    },
    formProse: {
        fontSize: 13,
        lineHeight: 19,
        fontFamily: fonts.regular,
        color: colors.textPrimary
    },
    formIntro: {
        backgroundColor: colors.surface,
        borderRadius: radius.md,
        borderWidth: 1,
        borderColor: colors.border,
        padding: spacing.base
    },
    formIntroText: {
        fontSize: 13,
        lineHeight: 19,
        fontFamily: fonts.regular,
        color: colors.textPrimary
    },
    signatureBlock: {
        marginTop: spacing.xs
    },
    signBtn: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        alignSelf: "flex-start",
        gap: spacing.xs,
        paddingHorizontal: spacing.base,
        paddingVertical: spacing.xs + 2,
        borderRadius: radius.full,
        backgroundColor: colors.teal400
    },
    signBtnText: {
        fontFamily: fonts.bold,
        fontSize: 13,
        color: colors.surface
    },
    signedRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.xs
    },
    signedText: {
        fontFamily: fonts.bold,
        fontSize: 13,
        color: colors.teal600
    },
    signLink: {
        marginLeft: spacing.xs
    },
    signLinkText: {
        fontFamily: fonts.medium,
        fontSize: 12,
        color: colors.textSecondary,
        textDecorationLine: "underline"
    }
});
